import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { problemMessage } from '../../api/baseApi'
import { useGrantDomainMutation, useListDomainsQuery, useRevokeDomainMutation, type DomainGrant } from '../../api/operator'
import { Loading, QueryError } from '../../ui/QueryState'
import { shown } from '../../api/useIntent'

/**
 * Which hostnames this tenant's sites may answer on.
 *
 * **A grant is the operator saying "this customer's host".** There is no
 * DNS challenge: the agency points the DNS itself, and the grant is what
 * lets the customer's own Site editor offer the host at all. A host
 * another tenant holds is refused, naming that tenant, because the
 * operator is trusted with the fact and has to know whose step comes
 * first.
 *
 * Revoking refuses while a site still answers on the host. The tenant
 * releases it from the site first — or the tenant is offboarded, which
 * removes grant and binding together — because revoking under a live
 * binding would leave a site stating an address nobody can reach, with
 * nobody told.
 */
export function TenantDomains({ tenant, active }: { tenant: string; active: boolean }) {
  const domains = useListDomainsQuery({ tenant })
  const [grant, granting] = useGrantDomainMutation()
  const [host, setHost] = useState('')
  const [revoking, setRevoking] = useState<DomainGrant | null>(null)

  if (domains.isLoading) return <Loading label="Loading domains…" />
  if (domains.error) return <QueryError error={domains.error} title="Could not load domains" />
  const granted = domains.data?.domains ?? []

  return (
    <Stack spacing={2}>
      {Boolean(granting.error) && <Alert severity="error">{problemMessage(granting.error, 'The domain was not granted.')}</Alert>}
      <Paper>
        {granted.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            No domains are granted to this tenant.
          </Typography>
        ) : (
          <List disablePadding>
            {granted.map((domain) => (
              <ListItem
                key={domain.host}
                divider
                secondaryAction={
                  <Tooltip title="Revoke">
                    <IconButton edge="end" aria-label={`Revoke ${domain.host}`} onClick={() => setRevoking(domain)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={domain.host}
                  secondary={`${bindingState(tenant, domain)} · granted ${new Date(domain.grantedAt).toLocaleDateString()} by ${domain.grantedBy}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      {active && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label="Domain"
            size="small"
            value={host}
            onChange={(event) => setHost(event.target.value)}
            helperText="A bare hostname, such as www.example.co.nz. Point its DNS at the site's hosting yourself."
            sx={{ minWidth: 320 }}
          />
          <Button
            variant="contained"
            disabled={!host.trim() || granting.isLoading}
            onClick={() =>
              grant({ tenant, grantDomain: { host: host.trim() } })
                .unwrap()
                .then(() => setHost(''))
                .catch(shown)
            }
          >
            Grant
          </Button>
        </Stack>
      )}
      {revoking && <RevokeDialog tenant={tenant} domain={revoking} onClose={() => setRevoking(null)} />}
    </Stack>
  )
}

/** What a grant's binding says, in a line: in use, by which site, or not. */
function bindingState(tenant: string, domain: DomainGrant): string {
  const binding = domain.binding
  if (!binding) return 'Not in use'
  const role = binding.canonical ? 'Main address' : 'Address'
  // A binding made before the host was granted can be another tenant's;
  // the operator needs to see that before revoking anything.
  if (binding.tenant !== tenant) return `${role} of a site in ${binding.tenant}`
  return `${role} of site ${binding.website}`
}

/**
 * One confirmation in front of a revoke, and the refusal inside it.
 *
 * A refusal is kept in the dialog rather than closing it, because the
 * 409 names the site still answering on the host — which is exactly what
 * the operator needs in front of them when deciding what to do next.
 */
function RevokeDialog({ tenant, domain, onClose }: { tenant: string; domain: DomainGrant; onClose: () => void }) {
  const [revoke, revoking] = useRevokeDomainMutation()
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Revoke {domain.host}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            <strong>{tenant}</strong>&rsquo;s sites will no longer be able to choose {domain.host} as a web address. A domain a
            site still answers on cannot be revoked: release it from the site first.
          </DialogContentText>
          {Boolean(revoking.error) && <Alert severity="error">{problemMessage(revoking.error, 'The domain was not revoked.')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={revoking.isLoading}
          onClick={() =>
            revoke({ tenant, host: domain.host })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Revoke {domain.host}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
